import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, FileText, Play, Layers } from "lucide-react";
import { MainLayout } from "../styles/Layouts";
import { PageTransition, FadeIn } from "../Components/AnimatedComponents";
import roles from "../data/roles";
import { RoleProject } from "../types";

type TabKey = "description" | "video" | "techStack";

interface TabDef {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
}

const allTabs: TabDef[] = [
  { key: "description", label: "What I Did", icon: <FileText size={16} /> },
  { key: "video", label: "Video Demo", icon: <Play size={16} /> },
  { key: "techStack", label: "Tech Stack", icon: <Layers size={16} /> },
];

const getAvailableTabs = (project: RoleProject): TabDef[] =>
  allTabs.filter((tab) => {
    if (tab.key === "description") return !!project.description;
    if (tab.key === "video") return !!project.videoUrl;
    if (tab.key === "techStack")
      return project.techStack && project.techStack.length > 0;
    return false;
  });

const tabContentVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2 } },
};

const RoleDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const role = roles.find((r) => r.slug === slug);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<TabKey>("description");
  const pillBarRef = useRef<HTMLDivElement>(null);

  const activeProject = role?.projects[activeIndex];
  const tabs = activeProject ? getAvailableTabs(activeProject) : [];
  const showNav = (role?.projects.length ?? 0) > 1;

  // Reset tab when switching projects
  useEffect(() => {
    const project = role?.projects[activeIndex];
    if (project) {
      const available = getAvailableTabs(project);
      if (available.length > 0) setActiveTab(available[0].key);
    }
  }, [activeIndex, role]);

  // Auto-scroll active pill into view on mobile
  useEffect(() => {
    if (pillBarRef.current) {
      const btn = pillBarRef.current.querySelector(
        ".nav-pill.active",
      ) as HTMLElement;
      btn?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  if (!role) {
    return (
      <PageTransition>
        <MainLayout>
          <NotFoundStyled>
            <h2>Role not found</h2>
            <Link to="/resume" className="back-link">
              <ArrowLeft size={18} />
              Back to Resume
            </Link>
          </NotFoundStyled>
        </MainLayout>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <MainLayout>
        <RoleDetailStyled>
          <FadeIn direction="up">
            <Link to="/resume" className="back-link">
              <ArrowLeft size={18} />
              Back to Resume
            </Link>
          </FadeIn>

          <FadeIn direction="up" delay={0.05}>
            <div className="role-header">
              <span className="date-badge">{role.year}</span>
              <h1>{role.title}</h1>
              <h3 className="company">{role.company}</h3>
              <p className="summary">{role.summary}</p>
            </div>
          </FadeIn>

          <FadeIn direction="up" delay={0.1}>
            <div className={`navigator ${!showNav ? "solo" : ""}`}>
              {/* ── Mobile pill bar ── */}
              {showNav && (
                <div className="pill-bar" ref={pillBarRef}>
                  {role.projects.map((project, i) => (
                    <button
                      key={project.name}
                      className={`nav-pill ${i === activeIndex ? "active" : ""}`}
                      onClick={() => setActiveIndex(i)}
                    >
                      {project.name}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Desktop sidebar ── */}
              {showNav && (
                <aside className="sidebar">
                  <div className="sidebar-header">Projects</div>
                  {role.projects.map((project, i) => (
                    <button
                      key={project.name}
                      className={`sidebar-item ${
                        i === activeIndex ? "active" : ""
                      }`}
                      onClick={() => setActiveIndex(i)}
                    >
                      <span className="sidebar-number">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="sidebar-name">{project.name}</span>
                    </button>
                  ))}
                </aside>
              )}

              {/* ── Detail panel ── */}
              <div className="detail-panel">
                <AnimatePresence exitBeforeEnter>
                  <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3 }}
                    className="detail-card"
                  >
                    {activeProject && (
                      <>
                        <h3 className="detail-title">{activeProject.name}</h3>

                        {tabs.length > 1 && (
                          <div className="tab-bar">
                            {tabs.map((tab) => (
                              <button
                                key={tab.key}
                                className={`tab-btn ${
                                  activeTab === tab.key ? "active" : ""
                                }`}
                                onClick={() => setActiveTab(tab.key)}
                              >
                                {tab.icon}
                                <span>{tab.label}</span>
                              </button>
                            ))}
                          </div>
                        )}

                        <div className="tab-content">
                          <AnimatePresence exitBeforeEnter>
                            <motion.div
                              key={activeTab}
                              variants={tabContentVariants}
                              initial="initial"
                              animate="animate"
                              exit="exit"
                            >
                              {activeTab === "description" &&
                                activeProject.description && (
                                  <div className="description-content">
                                    {activeProject.description
                                      .split("\n")
                                      .map((line, idx) => (
                                        <p key={idx}>{line}</p>
                                      ))}
                                  </div>
                                )}

                              {activeTab === "video" &&
                                activeProject.videoUrl && (
                                  <div className="video-content">
                                    <div className="video-wrapper">
                                      {activeProject.videoUrl.endsWith(
                                        ".mp4",
                                      ) ||
                                      activeProject.videoUrl.startsWith(
                                        "data:",
                                      ) ||
                                      activeProject.videoUrl.includes(
                                        "/static/media/",
                                      ) ? (
                                        <video
                                          src={activeProject.videoUrl}
                                          title={`${activeProject.name} demo`}
                                          controls
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: "8px",
                                          }}
                                        />
                                      ) : (
                                        <iframe
                                          src={`${activeProject.videoUrl}?autoplay=1`}
                                          title={`${activeProject.name} demo`}
                                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                          allowFullScreen
                                        />
                                      )}
                                    </div>
                                  </div>
                                )}

                              {activeTab === "techStack" &&
                                activeProject.techStack && (
                                  <div className="tech-content">
                                    {activeProject.techStack.map(
                                      (tech, idx) => (
                                        <motion.span
                                          key={tech}
                                          className="tech-chip"
                                          initial={{
                                            opacity: 0,
                                            scale: 0.85,
                                          }}
                                          animate={{ opacity: 1, scale: 1 }}
                                          transition={{
                                            delay: idx * 0.04,
                                            duration: 0.3,
                                          }}
                                        >
                                          {tech}
                                        </motion.span>
                                      ),
                                    )}
                                  </div>
                                )}
                            </motion.div>
                          </AnimatePresence>
                        </div>
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </FadeIn>
        </RoleDetailStyled>
      </MainLayout>
    </PageTransition>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────
const NotFoundStyled = styled.div`
  text-align: center;
  padding: 4rem 0;

  h2 {
    margin-bottom: 1.5rem;
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--primary-color);
    font-weight: 600;
    font-size: 0.9rem;
    transition: opacity 0.2s;
    &:hover {
      opacity: 0.8;
    }
  }
`;

const RoleDetailStyled = styled.section`
  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--primary-color);
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 2rem;
    transition: opacity 0.2s;
    &:hover {
      opacity: 0.8;
    }
  }

  .role-header {
    margin-bottom: 2.5rem;

    .date-badge {
      display: inline-block;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--primary-color);
      background: var(--background-light-color-2);
      padding: 0.25rem 0.85rem;
      border-radius: 100px;
      margin-bottom: 0.75rem;
      letter-spacing: 0.02em;
    }

    h1 {
      font-size: 2.5rem;
      margin-bottom: 0.25rem;

      @media screen and (max-width: 600px) {
        font-size: 1.75rem;
      }
    }

    .company {
      color: var(--primary-color-light);
      font-weight: 600;
      margin-bottom: 0.75rem;
    }

    .summary {
      color: var(--font-light-color);
      font-size: 1rem;
      line-height: 1.7;
      max-width: 700px;
    }
  }

  /* ── Navigator ── */
  .navigator {
    display: flex;
    gap: 2rem;

    &.solo {
      .detail-panel {
        max-width: 100%;
      }
    }
  }

  /* ── Mobile pill bar (hidden on desktop) ── */
  .pill-bar {
    display: none;
  }

  /* ── Desktop sidebar ── */
  .sidebar {
    width: 260px;
    flex-shrink: 0;
    position: sticky;
    top: 2rem;
    align-self: flex-start;
    max-height: calc(100vh - 4rem);
    overflow-y: auto;
    padding-right: 0.5rem;

    /* thin scrollbar */
    scrollbar-width: thin;
    scrollbar-color: var(--border-color) transparent;
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: var(--border-color);
      border-radius: 4px;
    }

    .sidebar-header {
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: var(--font-light-color);
      padding: 0 1rem;
      margin-bottom: 0.75rem;
      font-weight: 600;
    }

    .sidebar-item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
      padding: 0.7rem 1rem;
      background: transparent;
      border: none;
      border-radius: 10px;
      cursor: pointer;
      text-align: left;
      font-family: inherit;
      transition: all 0.2s ease;
      color: var(--font-light-color);
      font-size: 0.82rem;
      margin-bottom: 2px;

      .sidebar-number {
        font-size: 0.7rem;
        font-weight: 600;
        opacity: 0.4;
        flex-shrink: 0;
        min-width: 1.2rem;
      }

      .sidebar-name {
        font-weight: 500;
        line-height: 1.35;
      }

      &:hover {
        background: var(--background-light-color-2);
        color: var(--white-color);
      }

      &.active {
        background: var(--primary-color);
        color: #fff;

        span {
          color: #fff !important;
        }

        .sidebar-number {
          opacity: 0.7;
          color: #fff !important;
        }
      }
    }
  }

  /* ── Detail panel ── */
  .detail-panel {
    flex: 1;
    min-width: 0;
  }

  .detail-card {
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-radius: 16px;
    padding: 2rem;
  }

  .detail-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--white-color);
    margin: 0 0 1.5rem;
  }

  .tab-bar {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;

    .tab-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.5rem 1rem;
      border: 1px solid var(--border-color);
      border-radius: 10px;
      background: transparent;
      color: var(--font-light-color);
      font-family: inherit;
      font-size: 0.85rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.25s ease;

      &:hover {
        border-color: var(--primary-color);
        color: var(--primary-color);
      }

      &.active {
        background: var(--primary-color);
        border-color: var(--primary-color);
        color: #fff !important;

        span {
          color: #fff !important;
        }

        svg {
          color: #fff !important;
          stroke: #fff !important;
        }
      }

      svg {
        flex-shrink: 0;
      }
    }
  }

  .tab-content {
    min-height: 100px;
  }

  .description-content {
    p {
      font-size: 0.9rem;
      line-height: 1.8;
      color: var(--font-light-color);
      margin-bottom: 0.4rem;

      &:empty {
        margin-bottom: 0.8rem;
      }
    }
  }

  .video-content {
    .video-wrapper {
      position: relative;
      padding-bottom: 56.25%;
      height: 0;
      overflow: hidden;
      border-radius: 12px;
      border: 1px solid var(--border-color);

      iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
      }
    }
  }

  .tech-content {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;

    .tech-chip {
      padding: 0.5rem 1.1rem;
      background: var(--background-light-color-2);
      border: 1px solid var(--border-color);
      border-radius: 100px;
      font-size: 0.85rem;
      font-weight: 500;
      color: var(--white-color);
      transition: all 0.25s ease;

      &:hover {
        border-color: var(--primary-color);
        background: var(--primary-color);
        color: white;
      }
    }
  }

  /* ── Responsive ── */
  @media screen and (max-width: 900px) {
    .navigator {
      flex-direction: column;
      gap: 1.25rem;
    }

    .sidebar {
      display: none;
    }

    .pill-bar {
      display: flex;
      gap: 0.5rem;
      overflow-x: auto;
      padding-bottom: 0.5rem;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }

      .nav-pill {
        flex-shrink: 0;
        padding: 0.5rem 1rem;
        border: 1px solid var(--border-color);
        border-radius: 100px;
        background: transparent;
        color: var(--font-light-color);
        font-family: inherit;
        font-size: 0.8rem;
        font-weight: 500;
        cursor: pointer;
        white-space: nowrap;
        transition: all 0.2s ease;

        &:hover {
          border-color: var(--primary-color);
        }

        &.active {
          background: var(--primary-color);
          border-color: var(--primary-color);
          color: #fff;
        }
      }
    }

    .detail-card {
      padding: 1.5rem;
    }
  }

  @media screen and (max-width: 600px) {
    .detail-card {
      padding: 1.25rem;
    }

    .detail-title {
      font-size: 1.2rem;
    }
  }
`;

export default RoleDetailPage;
